import React, { useCallback, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import {
  DPIA_CRITERIA_CODE_MAX_LENGTH,
  DPIA_SCREENING_CRITERION_CODES,
  DPIA_TRIGGER_SOURCE_OPTIONS,
} from 'config/api/dpiaRequests/dpiaRequests.constants';
import type { DpiaRecommendation } from 'config/api/dpiaRequests/dpiaRequests.types';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import { canAccessDpiaNeedAssessmentPage } from '../_shared/assetRopaReviewAccess';
import AssetRopaWarningChips from '../_shared/AssetRopaWarningChips/AssetRopaWarningChips';
import ProcessingActivitySidePanel from '../_shared/ProcessingActivitySidePanel/ProcessingActivitySidePanel';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import {
  getLiveDecisionDeviation,
  getLiveRecommendation,
  hasValidationErrors,
  isDpiaRequestEditable,
  validateDecisionForm,
  validateDraftForm,
} from './dpiaNeedAssessment.helpers';
import useDpiaNeedAssessment from './useDpiaNeedAssessment';

type RouteParams = {
  apaId: string;
};

type LocationState = {
  returnPath?: string;
  sourceAssetProcessingActivityReviewId?: number;
  sourceRopaReviewRoundId?: number;
};

const getRecommendationBadgeClass = (recommendation: DpiaRecommendation, styles: ReturnType<typeof useStyles>) => {
  if (recommendation === 'dpia_strongly_recommended' || recommendation === 'dpia_recommended') {
    return styles.metricBadgeDanger;
  }

  if (recommendation === 'further_assessment_recommended') {
    return styles.metricBadgeWarn;
  }

  return styles.metricBadgeInfo;
};

const DpiaNeedAssessmentPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { apaId } = useParams<RouteParams>();
  const { enqueueSnackbar } = useSnackbar();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccess = canAccessDpiaNeedAssessmentPage(userData);
  const messages = processing_activities_messages.asset_ropa.dpia_need_assessment;

  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelComment, setCancelComment] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [validationErrors, setValidationErrors] = useState<Partial<Record<string, string>>>({});

  const returnPath = location.state?.returnPath ?? PATHS.PROCESSING_ACTIVITIES_ASSET_REVIEWS;

  const validationMessages = useMemo(
    () => ({
      required: t(validation_messages.required),
      triggerComment: t(messages.validation.trigger_comment),
      alwaysRuleComment: t(messages.validation.always_rule_comment),
      decisionComment: t(messages.validation.decision_comment),
      decisionRequired: t(messages.validation.decision_required),
      criteriaCodesMaxLength: t(messages.validation.criteria_codes_max_length, { max: DPIA_CRITERIA_CODE_MAX_LENGTH }),
    }),
    [messages, t],
  );

  const handleClose = useCallback(() => {
    history.push(returnPath);
  }, [history, returnPath]);

  const {
    allowNextNavigation,
    cancelMutation,
    context,
    decideNotRequiredMutation,
    decideRequiredMutation,
    formValues,
    handleNavigation,
    isDeciding,
    isError,
    isLoading,
    isSaving,
    NavigationPrompt,
    patchFormValues,
    refetch,
    request,
    saveMutation,
    toggleCriterion,
  } = useDpiaNeedAssessment({
    apaId: Number(apaId),
    onCancelSuccess: () => {
      enqueueSnackbar(t(messages.toasts.cancelled), { variant: 'success' });
      setCancelOpen(false);
    },
    onDecideSuccess: () => {
      enqueueSnackbar(t(messages.toasts.decided), { variant: 'success' });
    },
    onNavigate: handleClose,
    onSaveSuccess: () => {
      enqueueSnackbar(t(messages.toasts.saved), { variant: 'success' });
    },
    openInput: {
      sourceAssetProcessingActivityReviewId: location.state?.sourceAssetProcessingActivityReviewId,
      sourceRopaReviewRoundId: location.state?.sourceRopaReviewRoundId,
    },
    organizationId,
  });

  const editable = request ? isDpiaRequestEditable(request.status) : false;
  const liveRecommendation = formValues ? getLiveRecommendation(formValues) : 'normally_not_recommended';
  const decisionDeviates = formValues ? getLiveDecisionDeviation(formValues) : false;
  const showSummary = Boolean(formValues?.decision && formValues.decisionComment.trim());

  const assessedByPreview = useMemo(() => {
    if (request?.assessedByName) {
      return request.assessedByName;
    }

    const firstName = typeof userData.first_name === 'string' ? userData.first_name : '';
    const lastName = typeof userData.last_name === 'string' ? userData.last_name : '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || '—';
  }, [request?.assessedByName, userData.first_name, userData.last_name]);

  const canSaveDraft = useMemo(() => {
    if (!formValues) return false;
    return !hasValidationErrors(validateDraftForm(formValues, validationMessages));
  }, [formValues, validationMessages]);

  const saveDisabled = !canSaveDraft || isSaving || isDeciding;
  const saveDisabledTooltip = !canSaveDraft ? t(messages.validation.save_blocked) : '';

  const handleSave = useCallback(async () => {
    if (!formValues || !canSaveDraft) return;

    const errors = validateDraftForm(formValues, validationMessages);
    setValidationErrors(errors);

    if (hasValidationErrors(errors)) {
      enqueueSnackbar(t(messages.validation.save_blocked), { variant: 'warning' });
      return;
    }

    try {
      await saveMutation.mutateAsync(formValues);
    } catch {
      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [canSaveDraft, enqueueSnackbar, formValues, messages, saveMutation, t, validationMessages]);

  const handleConfirmDecision = useCallback(async () => {
    if (!formValues) return;

    const errors = validateDecisionForm(formValues, validationMessages);
    setValidationErrors(errors);

    if (hasValidationErrors(errors)) {
      enqueueSnackbar(t(messages.validation.save_blocked), { variant: 'warning' });
      return;
    }

    try {
      if (formValues.decision === 'dpia_required') {
        await decideRequiredMutation.mutateAsync(formValues);
      } else {
        await decideNotRequiredMutation.mutateAsync(formValues);
      }
      allowNextNavigation();
      handleClose();
    } catch {
      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [
    allowNextNavigation,
    decideNotRequiredMutation,
    decideRequiredMutation,
    enqueueSnackbar,
    formValues,
    handleClose,
    messages,
    t,
    validationMessages,
  ]);

  const handleCancelConfirm = useCallback(async () => {
    if (!cancelComment.trim()) {
      setCancelError(t(validation_messages.required));
      return;
    }

    try {
      await cancelMutation.mutateAsync(cancelComment.trim());
      allowNextNavigation();
      handleClose();
    } catch {
      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [allowNextNavigation, cancelComment, cancelMutation, enqueueSnackbar, handleClose, t]);

  let content: React.ReactNode;

  if (!canAccess) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(processing_activities_messages.asset_ropa.forbidden_body)}
      </Typography>
    );
  } else if (isLoading || !formValues || !request || !context) {
    content = <Loader inner />;
  } else if (isError) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <TextButton onClick={() => refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else {
    content = (
      <>
        <Box className={styles.reviewPanel}>
          <div className={styles.reviewPanelHeader}>
            <div>
              <div className={styles.reviewEyebrow}>{t(messages.statuses[request.status]).toUpperCase()}</div>
              <Typography variant='h3'>{t(messages.banner_title)}</Typography>
              <div className={styles.reviewMeta}>
                <span>
                  <strong>{t(messages.request_status)}:</strong>{' '}
                  <span className={`${styles.statusBadge} ${styles.statusBadgeWarn}`}>{t(messages.statuses[request.status])}</span>
                </span>
                <span>
                  <strong>{t(messages.asset_owner)}:</strong> {context.assetOwnerName ?? '—'}
                </span>
                <span>
                  <strong>{t(messages.dpia_indication)}:</strong>{' '}
                  {(() => {
                    const indicationMessages = processing_activities_messages.asset_ropa.dpia_status as Record<string, string[]>;
                    const indicationMessage = indicationMessages[context.dpiaIndicationStatus];
                    return indicationMessage ? t(indicationMessage) : context.dpiaIndicationStatus.replace(/_/g, ' ');
                  })()}
                </span>
              </div>
            </div>
            <div className={styles.metricsRow}>
              <div className={styles.metricCard}>
                <div className={styles.metricLabel}>{t(messages.previous_requests)}</div>
                <div className={styles.metricValue}>{context.previousRequestsCount}</div>
              </div>
              <div className={styles.metricCard}>
                <div className={styles.metricLabel}>{t(messages.previous_dpias)}</div>
                <div className={styles.metricValue}>{context.previousDpiasCount}</div>
              </div>
            </div>
          </div>
        </Box>

        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(messages.truth_note)}
        </Typography>

        <div className={styles.twoColumnLayout}>
          <aside>
            <ProcessingActivitySidePanel activity={context.centralProcessingActivity} />
            <Box className={styles.sidePanel} style={{ marginTop: 16 }}>
              <AssetRopaWarningChips warnings={context.warnings} />
              <Typography gutterBottom style={{ marginTop: 16 }} variant='h4'>
                {t(messages.history_title)}
              </Typography>
              {context.history.map(item => (
                <Box key={item.id} style={{ marginTop: 8 }}>
                  <Typography variant='body2'>
                    <strong>{item.label}</strong>
                  </Typography>
                  <Typography color='textSecondary' variant='body2'>
                    {item.subtitle}
                  </Typography>
                </Box>
              ))}
            </Box>
          </aside>

          <section className={styles.mainPanel}>
            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 1</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.trigger.title)}
              </Typography>
              <FormControl disabled={!editable} fullWidth margin='normal' variant='outlined'>
                <InputLabel>{t(messages.sections.trigger.source)}</InputLabel>
                <Select
                  label={t(messages.sections.trigger.source)}
                  onChange={event => patchFormValues({ triggerSource: event.target.value as typeof formValues.triggerSource })}
                  value={formValues.triggerSource}
                >
                  {DPIA_TRIGGER_SOURCE_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {t(messages.trigger_sources[option])}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.triggerComment)}
                fullWidth
                helperText={validationErrors.triggerComment}
                label={t(messages.sections.trigger.comment)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ triggerComment: event.target.value })}
                rows={4}
                value={formValues.triggerComment}
                variant='outlined'
              />
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewPanelHeader}>
                <div>
                  <div className={styles.reviewEyebrow}>SECTION 2</div>
                  <Typography variant='h3'>{t(messages.sections.criteria.title)}</Typography>
                </div>
                <Chip label={t(messages.sections.criteria.selected_count, { count: formValues.selectedCriteria.length })} />
              </div>
              <div className={styles.assetList}>
                {DPIA_SCREENING_CRITERION_CODES.map(code => (
                  <Box key={code} className={styles.purposePanel}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formValues.selectedCriteria.includes(code)}
                          color='primary'
                          disabled={!editable}
                          onChange={() => toggleCriterion(code)}
                        />
                      }
                      label={
                        <span>
                          <Typography variant='body2'>
                            <strong>{t(messages.criteria[code])}</strong>
                          </Typography>
                          {code === 'other_high_risk' && (
                            <Typography color='textSecondary' variant='caption'>
                              {t(messages.sections.criteria.criterion_ten_help)}
                            </Typography>
                          )}
                        </span>
                      }
                    />
                  </Box>
                ))}
              </div>
              {validationErrors.selectedCriteria && (
                <FormHelperText error style={{ marginTop: 8 }}>
                  {validationErrors.selectedCriteria}
                </FormHelperText>
              )}
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.criteriaAssessmentComment)}
                fullWidth
                helperText={validationErrors.criteriaAssessmentComment}
                label={t(messages.sections.criteria.comment)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ criteriaAssessmentComment: event.target.value })}
                rows={4}
                value={formValues.criteriaAssessmentComment}
                variant='outlined'
              />
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 3</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.always_rule.title)}
              </Typography>
              <Box className={styles.purposePanel}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.alwaysDpiaRuleTriggered}
                      color='primary'
                      disabled={!editable}
                      onChange={event =>
                        patchFormValues({
                          alwaysDpiaRuleTriggered: event.target.checked,
                          alwaysDpiaRuleComment: event.target.checked ? formValues.alwaysDpiaRuleComment : '',
                        })
                      }
                    />
                  }
                  label={
                    <span>
                      <Typography variant='body2'>
                        <strong>{t(messages.sections.always_rule.checkbox)}</strong>
                      </Typography>
                      <Typography color='textSecondary' variant='caption'>
                        {t(messages.sections.always_rule.help)}
                      </Typography>
                    </span>
                  }
                />
              </Box>
              <TextField
                disabled={!editable || !formValues.alwaysDpiaRuleTriggered}
                error={Boolean(validationErrors.alwaysDpiaRuleComment)}
                fullWidth
                helperText={validationErrors.alwaysDpiaRuleComment}
                label={t(messages.sections.always_rule.comment)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ alwaysDpiaRuleComment: event.target.value })}
                rows={3}
                value={formValues.alwaysDpiaRuleComment}
                variant='outlined'
              />
            </Box>

            <Box className={`${styles.apaSection} ${styles.reviewPanel}`}>
              <div className={styles.reviewPanelHeader}>
                <div>
                  <div className={styles.reviewEyebrow}>SECTION 4</div>
                  <Typography variant='h3'>{t(messages.sections.recommendation.title)}</Typography>
                </div>
                <span className={`${styles.metricBadge} ${getRecommendationBadgeClass(liveRecommendation, styles)}`}>
                  {t(messages.recommendations[liveRecommendation])}
                </span>
              </div>
              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>{t(messages.sections.recommendation.selected_criteria)}</div>
                  <div className={styles.metricValue}>{formValues.selectedCriteria.length}</div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>{t(messages.sections.recommendation.always_rule)}</div>
                  <div className={styles.metricValue} style={{ fontSize: '1.1rem' }}>
                    {formValues.alwaysDpiaRuleTriggered ? t(messages.yes) : t(messages.no)}
                  </div>
                </div>
                <div className={styles.metricCard}>
                  <div className={styles.metricLabel}>{t(messages.sections.recommendation.title)}</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, marginTop: 8 }}>{t(messages.recommendations[liveRecommendation])}</div>
                </div>
              </div>
              <Box className={styles.reviewBanner}>
                <Typography variant='body2'>{t(messages.sections.recommendation.notice)}</Typography>
              </Box>
            </Box>

            <Box className={styles.apaSection}>
              <div className={styles.reviewEyebrow}>SECTION 5</div>
              <Typography gutterBottom variant='h3'>
                {t(messages.sections.decision.title)}
              </Typography>
              <FormControl component='fieldset' disabled={!editable} error={Boolean(validationErrors.decision)} fullWidth margin='normal'>
                <RadioGroup
                  onChange={event => patchFormValues({ decision: event.target.value as typeof formValues.decision })}
                  value={formValues.decision}
                >
                  <Box className={styles.purposePanel}>
                    <FormControlLabel
                      control={<Radio color='primary' />}
                      label={t(messages.sections.decision.required)}
                      value='dpia_required'
                    />
                  </Box>
                  <Box className={styles.purposePanel}>
                    <FormControlLabel
                      control={<Radio color='primary' />}
                      label={t(messages.sections.decision.not_required)}
                      value='dpia_not_required'
                    />
                  </Box>
                </RadioGroup>
                {validationErrors.decision && <FormHelperText>{validationErrors.decision}</FormHelperText>}
              </FormControl>
              <TextField
                disabled={!editable}
                error={Boolean(validationErrors.decisionComment)}
                fullWidth
                helperText={validationErrors.decisionComment}
                label={t(messages.sections.decision.comment)}
                margin='normal'
                multiline
                onChange={event => patchFormValues({ decisionComment: event.target.value })}
                rows={4}
                value={formValues.decisionComment}
                variant='outlined'
              />
              {decisionDeviates && editable && (
                <Box className={styles.reviewBanner}>
                  <Typography variant='body2'>{t(messages.sections.decision.deviation_warning)}</Typography>
                </Box>
              )}
              <div className={styles.filtersRow}>
                <TextField
                  disabled
                  fullWidth
                  label={t(messages.sections.decision.assessed_by)}
                  margin='normal'
                  value={assessedByPreview}
                  variant='outlined'
                />
                <TextField
                  disabled
                  fullWidth
                  label={t(messages.sections.decision.assessed_at)}
                  margin='normal'
                  value={request.assessedAt ?? '—'}
                  variant='outlined'
                />
              </div>

              {showSummary && editable && (
                <Box className={styles.closureBanner}>
                  <Typography gutterBottom variant='h4'>
                    {t(messages.sections.decision.summary_title)}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>{t(messages.sections.recommendation.selected_criteria)}:</strong> {formValues.selectedCriteria.length}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>{t(messages.sections.recommendation.always_rule)}:</strong>{' '}
                    {formValues.alwaysDpiaRuleTriggered ? t(messages.yes) : t(messages.no)}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>{t(messages.sections.recommendation.title)}:</strong> {t(messages.recommendations[liveRecommendation])}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>{t(messages.sections.decision.title)}:</strong>{' '}
                    {formValues.decision === 'dpia_required'
                      ? t(messages.sections.decision.required)
                      : t(messages.sections.decision.not_required)}
                  </Typography>
                  {formValues.decision === 'dpia_required' && (
                    <>
                      <Typography variant='body2'>
                        <strong>{t(messages.sections.decision.responsible_owner)}:</strong> {context.assetOwnerName ?? '—'}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>{t(messages.sections.decision.created_dpia_title)}:</strong> {context.proposedDpiaTitle}
                      </Typography>
                    </>
                  )}
                </Box>
              )}

              {request.createdDpia && (
                <Box className={styles.closureBanner}>
                  <Typography gutterBottom variant='h4'>
                    {t(messages.created_dpia.title)}
                  </Typography>
                  <Typography variant='body2'>
                    <strong>{request.createdDpia.title}</strong>
                  </Typography>
                  <Typography color='textSecondary' variant='body2'>
                    {t(messages.created_dpia.status)}: {request.createdDpia.status} · {t(messages.created_dpia.responsible)}:{' '}
                    {request.createdDpia.responsibleName}
                  </Typography>
                  <Button
                    onClick={() =>
                      history.push({
                        pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_OWNER_DPIA, {
                          dpiaId: String(request.createdDpia?.id),
                        }),
                        state: { returnPath: history.location.pathname },
                      })
                    }
                    style={{ marginTop: 12 }}
                    variant='outlined'
                  >
                    {t(messages.created_dpia.open)}
                  </Button>
                </Box>
              )}

              {editable && (
                <div className={styles.pageActions}>
                  <Tooltip title={saveDisabledTooltip}>
                    <Box className={styles.tooltipTrigger} component='span'>
                      <ColoredButton
                        className=''
                        customColor='secondary'
                        disabled={saveDisabled}
                        icon={false}
                        onClick={handleSave}
                        showLoader={isSaving}
                        textLike={false}
                        variant='outlined'
                      >
                        {t(messages.save_and_continue)}
                      </ColoredButton>
                    </Box>
                  </Tooltip>
                  {formValues.decision === 'dpia_required' && (
                    <ColoredButton
                      className=''
                      customColor='secondary'
                      disabled={isDeciding || isSaving}
                      icon={false}
                      onClick={handleConfirmDecision}
                      showLoader={isDeciding}
                      textLike={false}
                      variant='contained'
                    >
                      {t(messages.sections.decision.confirm_required)}
                    </ColoredButton>
                  )}
                  {formValues.decision === 'dpia_not_required' && (
                    <ColoredButton
                      className=''
                      customColor='secondary'
                      disabled={isDeciding || isSaving}
                      icon={false}
                      onClick={handleConfirmDecision}
                      showLoader={isDeciding}
                      textLike={false}
                      variant='contained'
                    >
                      {t(messages.sections.decision.confirm_not_required)}
                    </ColoredButton>
                  )}
                </div>
              )}
            </Box>
          </section>
        </div>
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          backButton
          noBorder
          onBack={() => handleNavigation()}
          rightAdornment={
            editable ? (
              <Box className={styles.headerActions}>
                <Tooltip title={saveDisabledTooltip}>
                  <Box className={styles.tooltipTrigger} component='span'>
                    <ColoredButton
                      className=''
                      customColor='secondary'
                      disabled={saveDisabled}
                      icon={false}
                      onClick={handleSave}
                      showLoader={isSaving}
                      textLike={false}
                      variant='outlined'
                    >
                      {t(messages.save_and_continue)}
                    </ColoredButton>
                  </Box>
                </Tooltip>
                <ColoredButton
                  className=''
                  customColor='grey'
                  disabled={cancelMutation.isLoading}
                  icon={false}
                  onClick={() => setCancelOpen(true)}
                  showLoader={false}
                  textLike={false}
                  variant='outlined'
                >
                  {t(messages.cancel_request)}
                </ColoredButton>
              </Box>
            ) : undefined
          }
          subtitle={t(messages.page_subtitle)}
        >
          {t(messages.page_title)}
        </ManagePageHeader>
        {content}
        <Dialog fullWidth maxWidth='sm' onClose={() => setCancelOpen(false)} open={cancelOpen}>
          <DialogTitle>{t(messages.modals.cancel_request.title)}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              error={Boolean(cancelError)}
              fullWidth
              helperText={cancelError || t(messages.modals.cancel_request.help)}
              label={t(messages.modals.cancel_request.comment)}
              multiline
              onChange={event => {
                setCancelComment(event.target.value);
                if (cancelError) setCancelError('');
              }}
              rows={4}
              value={cancelComment}
              variant='outlined'
            />
          </DialogContent>
          <DialogActions>
            <ColoredButton
              className=''
              customColor='grey'
              disabled={cancelMutation.isLoading}
              icon={false}
              onClick={() => setCancelOpen(false)}
              showLoader={false}
              textLike={false}
              variant='outlined'
            >
              {t(general_messages.cancel)}
            </ColoredButton>
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={cancelMutation.isLoading}
              icon={false}
              onClick={handleCancelConfirm}
              showLoader={cancelMutation.isLoading}
              textLike={false}
              variant='contained'
            >
              {t(messages.modals.cancel_request.confirm)}
            </ColoredButton>
          </DialogActions>
        </Dialog>
        <NavigationPrompt />
      </Container>
    </ErrorBoundary>
  );
};

export default DpiaNeedAssessmentPage;
